// frontend/src/pages/CourseDetailPage.jsx
// Individual course detail page

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../contexts/AuthContext';
import { Clock, Users, Star, CheckCircle, PlayCircle, IndianRupee, Lock } from 'lucide-react';
import VideoPlayer from '../components/VideoPlayer';
import AssessmentPanel from '../components/AssessmentPanel';
import { coursesAPI, userAPI } from '../utils/api';
import { formatCurrency } from '../lib/utils';

const formatDuration = seconds => seconds ? `${Math.max(1, Math.round(seconds / 60))} min` : null;

const CourseDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isSignedIn } = useUser();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessState, setAccessState] = useState('idle');
  const [accessExpiresAt, setAccessExpiresAt] = useState(null);
  const [progress, setProgress] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null); // { moduleIndex, lessonIndex }
  const [lessonLoading, setLessonLoading] = useState('');
  const [learningError, setLearningError] = useState('');
  const courseId = course?.id;

  useEffect(() => {
    fetchCourse();
  }, [slug]);

  const checkAccess = useCallback(async () => {
    if (!courseId) return;
    try {
      setAccessState('loading');
      const response = await userAPI.checkCourseAccess(courseId);
      setHasAccess(true);
      setAccessExpiresAt(response.data.data.expires_at || null);
    } catch (error) {
      setHasAccess(false);
      if (![403, 404].includes(error.response?.status)) setLearningError('Course access could not be checked.');
    } finally {
      setAccessState('ready');
    }
  }, [courseId]);

  useEffect(() => {
    if (isSignedIn && courseId) checkAccess();
  }, [isSignedIn, courseId, checkAccess]);

  const fetchProgress = useCallback(async () => {
    if (!courseId || !hasAccess) return;
    try {
      const res = await userAPI.getCourseWithProgress(courseId);
      const lessons = res.data.data.curriculum.flatMap((section) => section.lessons);
      setCourse((current) => ({ ...current, curriculum: res.data.data.curriculum.map((section) => ({ ...section, moduleTitle: section.title })), assessments: res.data.data.assessments }));
      setProgress({ completedLessons: lessons.filter((lesson) => lesson.progress?.status === 'completed').map((lesson) => lesson.id), progressPercentage: res.data.data.progress_percent || 0 });
    } catch (error) {
      setLearningError(error.response?.data?.message || 'Your learning content could not be loaded.');
    }
  }, [courseId, hasAccess]);

  useEffect(() => {
    if (hasAccess && courseId) fetchProgress();
  }, [hasAccess, courseId, fetchProgress]);

  const fetchCourse = async () => {
    try {
      const response = await coursesAPI.getBySlug(slug);
      const item = response.data.data;
      setCourse({ ...item, _id: item.id, shortDescription: item.short_description, description: item.description_html?.replace(/<[^>]*>/g, '') || '', price: (item.price?.list_amount || 0) / 100, discountedPrice: item.price?.sale_amount == null ? null : item.price.sale_amount / 100, instructor: item.instructors?.[0]?.name, thumbnail: { url: item.thumbnail_url }, previewVideo: item.trailer_url });
    } catch (error) {
      console.error('Error fetching course:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLessonClick = async (moduleIndex, lessonIndex) => {
    if (!hasAccess) return;
    const mod = course?.curriculum?.[moduleIndex];
    const lesson = mod?.lessons?.[lessonIndex];
    if (!lesson) return;
    try {
      setLessonLoading(lesson.id); setLearningError('');
      const detail = await userAPI.getLesson(lesson.id);
      let videoUrl = null;
      if (detail.data.data.video?.playback_url) {
        const playback = await userAPI.resolvePlayback(detail.data.data.video.playback_url);
        videoUrl = playback.data.data.url;
      }
      setCourse((current) => {
        const curriculum = current.curriculum.map((section, sectionIndex) => ({ ...section, lessons: section.lessons.map((item, itemIndex) => sectionIndex === moduleIndex && itemIndex === lessonIndex ? { ...item, videoUrl, resources: detail.data.data.resources } : item) }));
        return { ...current, curriculum };
      });
      setSelectedLesson({ moduleIndex, lessonIndex });
    } catch (error) {
      setLearningError(error.response?.status === 409 ? 'This video is still processing. Please try again later.' : error.response?.data?.message || 'This lesson could not be opened.');
    } finally {
      setLessonLoading('');
    }
  };

  const handleMarkComplete = async () => {
    if (!hasAccess || !selectedLesson || !course) return;
    const lesson = course.curriculum?.[selectedLesson.moduleIndex]?.lessons?.[selectedLesson.lessonIndex];
    const id = lesson?.id;
    try {
      setLearningError('');
      await userAPI.updateProgress(id, { progress_seconds: lesson.duration_seconds || 0, completed: true });
      await fetchProgress();
    } catch (e) {
      setLearningError(e.response?.data?.message || 'Progress could not be saved.');
    }
  };

  const isLessonCompleted = (mi, li) =>
    progress?.completedLessons?.includes(course?.curriculum?.[mi]?.lessons?.[li]?.id) ?? false;

  const handlePurchase = () => {
    if (!isSignedIn) {
      navigate('/sign-in');
      return;
    }
    
    // Navigate to checkout page
    navigate(`/checkout/${slug}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black pt-16">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black pt-16">
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">Course not found</h1>
          <button
            onClick={() => navigate('/courses')}
            className="px-6 py-3 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  const finalPrice = course.discountedPrice ?? course.price;
  const hasDiscount = course.discountedPrice !== null && course.discountedPrice < course.price;

  return (
    <div className="min-h-screen bg-black pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {learningError && <div role="alert" className="rounded-lg border border-red-600 bg-red-950/40 p-4 text-red-200">{learningError}</div>}
            {hasAccess && progress && <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"><div className="flex justify-between text-sm"><span>Course progress</span><span className="text-yellow-400">{progress.progressPercentage}%</span></div><div className="mt-2 h-2 rounded bg-zinc-800"><div className="h-2 rounded bg-yellow-400" style={{ width: `${Math.min(100, progress.progressPercentage)}%` }}/></div></div>}
            {/* Video Preview / Lesson Player */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3"
            >
              {(() => {
                if (hasAccess && selectedLesson) {
                  const mod = course.curriculum?.[selectedLesson.moduleIndex];
                  const lesson = mod?.lessons?.[selectedLesson.lessonIndex];
                  if (lesson?.videoUrl) {
                    const completed = isLessonCompleted(selectedLesson.moduleIndex, selectedLesson.lessonIndex);
                    return (
                      <>
                        <VideoPlayer
                          videoUrl={lesson.videoUrl}
                          title={lesson.title}
                        />
                        {!completed && (
                          <button
                            type="button"
                            onClick={handleMarkComplete}
                            className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-2.5 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
                          >
                            <CheckCircle className="w-5 h-5" />
                            Mark as complete
                          </button>
                        )}
                      </>
                    );
                  }
                }
                if (course.previewVideo) {
                  return (
                    <VideoPlayer
                      videoUrl={course.previewVideo}
                      title={course.title}
                    />
                  );
                }
                return (
                  <div className="aspect-video bg-zinc-900 rounded-lg flex items-center justify-center">
                    <PlayCircle className="w-20 h-20 text-gray-600" />
                  </div>
                );
              })()}
            </motion.div>

            {/* Course Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h1 className="text-4xl font-bold text-white mb-4">{course.title}</h1>
              <p className="text-gray-400 text-lg mb-6">{course.shortDescription}</p>

              <div className="flex flex-wrap items-center gap-6 text-gray-400 mb-6">
                {course.duration && (
                  <div className="flex items-center space-x-2">
                    <Clock className="w-5 h-5" />
                    <span>{course.duration}</span>
                  </div>
                )}
                {course.totalEnrollments > 0 && (
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5" />
                    <span>{course.totalEnrollments} students enrolled</span>
                  </div>
                )}
                {course.rating && (
                  <div className="flex items-center space-x-2">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <span>{course.rating} rating</span>
                  </div>
                )}
              </div>

              {course.level && (
                <span className="inline-block px-4 py-2 bg-yellow-500/10 text-yellow-400 rounded-lg font-medium mb-6">
                  {course.level} Level
                </span>
              )}
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-zinc-900 rounded-xl p-6 border border-zinc-800"
            >
              <h2 className="text-2xl font-bold text-white mb-4">About This Course</h2>
              <p className="text-gray-400 whitespace-pre-line">{course.description}</p>
            </motion.div>

            {/* Curriculum */}
            {course.curriculum && course.curriculum.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-zinc-900 rounded-xl p-6 border border-zinc-800"
              >
                <h2 className="text-2xl font-bold text-white mb-6">Course Curriculum</h2>
                <div className="space-y-4">
                  {course.curriculum.map((module, moduleIndex) => (
                    <div key={moduleIndex} className="border border-zinc-800 rounded-lg overflow-hidden">
                      <div className="bg-zinc-800/50 p-4">
                        <h3 className="text-lg font-semibold text-white">
                          {module.moduleTitle}
                        </h3>
                      </div>
                      <div className="p-4 space-y-2">
                        {module.lessons.map((lesson, lessonIndex) => {
                          const canOpen = hasAccess;
                          const completed = isLessonCompleted(moduleIndex, lessonIndex);
                          const isSelected =
                            selectedLesson?.moduleIndex === moduleIndex &&
                            selectedLesson?.lessonIndex === lessonIndex;
                          return (
                            <button
                              key={lessonIndex}
                              type="button"
                              onClick={() => handleLessonClick(moduleIndex, lessonIndex)}
                              disabled={!canOpen || lessonLoading === lesson.id}
                              className={`w-full flex items-center justify-between py-2 px-3 rounded-lg text-left transition-colors ${
                                canOpen
                                  ? 'hover:bg-zinc-800/80 cursor-pointer'
                                  : 'cursor-not-allowed opacity-75'
                              } ${isSelected ? 'bg-yellow-500/10 ring-1 ring-yellow-500/50' : ''}`}
                            >
                              <div className="flex items-center space-x-3">
                                {canOpen ? (
                                  completed ? (
                                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                                  ) : (
                                    <PlayCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                                  )
                                ) : (
                                  <Lock className="w-5 h-5 text-gray-500 flex-shrink-0" />
                                )}
                                <span className="text-gray-300">{lessonLoading === lesson.id ? 'Opening lesson…' : lesson.title}</span>
                              </div>
                              {formatDuration(lesson.duration_seconds) && (
                                <span className="text-gray-500 text-sm">{formatDuration(lesson.duration_seconds)}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {hasAccess && course.assessments && <AssessmentPanel assessments={course.assessments} />}

            {/* Instructor */}
            {course.instructor && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-zinc-900 rounded-xl p-6 border border-zinc-800"
              >
                <h2 className="text-2xl font-bold text-white mb-4">Instructor</h2>
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center">
                    <span className="text-2xl">👨‍🏫</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{course.instructor}</h3>
                    <p className="text-gray-400">Expert Trading Mentor</p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar - Purchase Card */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="sticky top-24 bg-zinc-900 rounded-xl p-6 border border-zinc-800"
            >
              {course.thumbnail?.url && (
                <img
                  src={course.thumbnail.url}
                  alt={course.title}
                  className="w-full aspect-video object-cover rounded-lg mb-6"
                />
              )}

              <div className="mb-6">
                <div className="flex items-center space-x-3 mb-2">
                  {hasDiscount && (
                    <span className="text-2xl text-gray-500 line-through">
                      {formatCurrency(course.price)}
                    </span>
                  )}
                  <span className="text-4xl font-bold text-yellow-400 flex items-center">
                    <IndianRupee className="w-8 h-8" />
                    {finalPrice}
                  </span>
                </div>
                {hasDiscount && (
                  <span className="inline-block px-3 py-1 bg-green-500/10 text-green-400 rounded-full text-sm font-medium">
                    Save {formatCurrency(course.price - course.discountedPrice)}
                  </span>
                )}
              </div>

              {hasAccess ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full py-4 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>Go to Dashboard</span>
                </button>
              ) : (
                <button
                  onClick={handlePurchase}
                  disabled={accessState === 'loading'}
                  className="w-full py-4 bg-yellow-500 text-black rounded-lg font-semibold hover:bg-yellow-600 transition-colors"
                >
                  {accessState === 'loading' ? 'Checking access…' : 'Buy Now'}
                </button>
              )}

              <div className="mt-6 space-y-3">
                {hasAccess && accessExpiresAt && <p className="text-sm text-amber-300">Access through {new Date(accessExpiresAt).toLocaleDateString('en-IN')}</p>}
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-sm">Access follows your purchase or subscription terms</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-sm">Certificate of completion</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-sm">Access on mobile and desktop</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailPage;
